// Шкала индикатора уровня: арифметика без DOM.
//
// Отдельно от отрисовки ради тестов: ошибиться здесь легко и незаметно —
// метка порога, съехавшая на двенадцать децибел, выглядит правдоподобно, а
// ставить по ней порог значит получить тишину без единого сообщения.

/** Низ шкалы. Тише −60 дБFS речь с микрофона не бывает, это уже шум тракта. */
export const FLOOR_DB = -60;

/** Сколько пик держится на месте, прежде чем начать спадать. */
export const PEAK_HOLD_S = 1;
/** Скорость спада пика после удержания. */
export const PEAK_FALL_DB_S = 20;
/** Скорость спада самой полосы. Атака мгновенная, спад плавный — иначе мерцает. */
export const BAR_FALL_DB_S = 40;

/** Линейный уровень в дБFS, не ниже пола шкалы. */
export function toDb(linear: number): number {
  if (!(linear > 0)) {
    return FLOOR_DB;
  }
  return Math.max(FLOOR_DB, 20 * Math.log10(linear));
}

/**
 * Децибелы для подписи: целые и с настоящим минусом, как на оси и в
 * остальных подписях страницы, а не с дефисом.
 */
export function formatDb(db: number): string {
  const rounded = Math.round(db);
  return rounded < 0 ? `−${-rounded} дБ` : `${rounded} дБ`;
}

/** Доля шкалы 0..1 для уровня в дБFS. */
export function position(db: number): number {
  return Math.min(1, Math.max(0, (db - FLOOR_DB) / -FLOOR_DB));
}

/**
 * Сглаженное состояние полосы: текущий уровень и удерживаемый пик, в дБFS.
 *
 * Изменяется на месте: показания приходят двадцать раз в секунду, и заводить
 * на каждое новый объект незачем.
 */
export interface Ballistics {
  barDb: number;
  peakDb: number;
  /** Сколько ещё держать пик, секунды. */
  peakHold: number;
}

export function freshBallistics(): Ballistics {
  return { barDb: FLOOR_DB, peakDb: FLOOR_DB, peakHold: 0 };
}

/**
 * Учесть новое показание, пришедшее через `dt` секунд после прошлого.
 *
 * Полоса идёт за среднеквадратичным уровнем — с ним сравниваются пороги. Тик
 * пика — за пиком, чтобы клиппинг был виден, даже когда средний уровень скромен.
 */
export function step(state: Ballistics, rms: number, peak: number, dt: number): void {
  const bar = toDb(rms);
  state.barDb = bar >= state.barDb ? bar : Math.max(bar, state.barDb - BAR_FALL_DB_S * dt);

  const top = toDb(peak);
  if (top >= state.peakDb) {
    state.peakDb = top;
    state.peakHold = PEAK_HOLD_S;
    return;
  }
  if (state.peakHold > 0) {
    state.peakHold = Math.max(0, state.peakHold - dt);
    return;
  }
  state.peakDb = Math.max(top, state.peakDb - PEAK_FALL_DB_S * dt);
}

/** То, что нужно знать о настройках, чтобы поставить метку порога. */
export interface ThresholdSettings {
  inputGainDb: number;
  /** 0 — гейт выключен. */
  gateDb: number;
  vad: "off" | "energy" | "silero";
  vadThreshold: number;
}

export interface Marker {
  db: number;
  label: string;
}

/**
 * Метки порогов на шкале входа.
 *
 * Для энергетического VAD шкала та же, что у полосы: он сравнивает
 * среднеквадратичный уровень окна. Гейт — только приблизительно: он смотрит
 * на огибающую `|x|` с постоянной 5 мс, да ещё после обрезного фильтра и
 * шумодава, а полоса показывает RMS за 50 мс. На пиках слогов огибающая выше
 * RMS, и гейт открывается раньше, чем полоса дойдёт до черты. Черта гейта —
 * ориентир с точностью в несколько децибел, а не граница.
 *
 * Индикатор меряет в браузере, **до** `input_gain_db`, а сервер сравнивает
 * порог уже после усиления, первым узлом тракта. Поэтому метка сдвинута на
 * усиление вниз: при +12 дБ гейт на −40 срабатывает на том, что браузер видит
 * как −52. Без поправки метка врала бы ровно на величину усиления — та самая
 * ловушка, которую ревью PR #9 поймало в тексте гайда.
 *
 * У Silero метки нет: его порог — вероятность речи, и на шкале уровня ему
 * места не существует.
 */
export function thresholdMarkers(settings: ThresholdSettings): Marker[] {
  const markers: Marker[] = [];
  if (settings.gateDb !== 0) {
    markers.push({ db: settings.gateDb - settings.inputGainDb, label: "гейт" });
  }
  if (settings.vad === "energy" && settings.vadThreshold > 0) {
    markers.push({
      db: 20 * Math.log10(settings.vadThreshold) - settings.inputGainDb,
      label: "VAD",
    });
  }
  return markers;
}
